/**
 * Central export file for all Jotai atoms and actions
 *
 * This file provides a single entry point for importing atoms throughout the application.
 * Organized into three main sections:
 * 1. Core Atoms - State management for all entities
 * 2. Action Atoms - Functions for data fetching and mutations
 * 3. Persistence Atoms - Auto-save and dirty state tracking
 */

import { atom } from "jotai";

// ============================================================================
// STORE
// ============================================================================

export { getAtomStore, resetAtomStore, subscribeToStoreReset } from "./store";

// ============================================================================
// CORE ATOMS - Re-export all core atoms
// ============================================================================

// User & Auth
export * from "./core/user";

// Workspaces
export * from "./core/workspaces";

// Agents
export * from "./core/agents";

// Workflows
export * from "./core/workflows";

// Patients
export * from "./core/patients";

// Knowledge Bases
export * from "./core/knowledge-bases";

// Follow-ups
export * from "./core/follow-ups";

// Phases
export * from "./core/phases";

// History (Undo/Redo)
export * from "./core/history";

// Call Sessions
export * from "./core/call-sessions";

// Call Attempts
export * from "./core/call-attempts";

// Triggered Alerts
export * from "./core/triggered-alerts";

// Mock data flag
export const useMockDataAtom = atom<boolean>(false);

// ============================================================================
// DERIVED ATOMS - Re-export all derived/computed atoms
// ============================================================================

// Workflow Derived Atoms
export * from "./derived/workflows";

// ============================================================================
// ACTION ATOMS - Re-export all action functions
// ============================================================================

// User Actions
export * from "./actions/user-actions";

// Workspace Actions
export * from "./actions/workspace-actions";

// Agent Actions
export * from "./actions/agent-actions";

// Workflow Actions
export * from "./actions/workflow-actions";

// Phase Actions
export * from "./actions/phase-actions";

// History Actions (Undo/Redo)
export * from "./actions/history-actions";

// Knowledge Base Actions
export * from "./actions/knowledge-base-actions";

// Workflow Generation State
export * from "./core/workflow-generation";

// Patient Actions
export * from "./actions/patient-actions";

// Follow-up Actions
export * from "./actions/follow-up-actions";

// Call Session Actions
export * from "./actions/call-session-actions";

// Call Attempt Actions
export * from "./actions/call-attempt-actions";

// Triggered Alert Actions
export * from "./actions/triggered-alert-actions";

// Store Core Atoms
export * from "./core/store";
export { currentListingResourceAtom, currentListingReviewsAtom } from "./core/store";

// My Resources Core Atoms
export * from "./core/my-resources";

// Publish Form Core Atoms
export * from "./core/publish-form";

// Store Actions
export * from "./actions/store-actions";

// ============================================================================
// PERSISTENCE ATOMS - Re-export all persistence utilities
// ============================================================================

// Auto-save
export * from "./persistence/auto-save";
